import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, ExternalLink, Clock, User as UserIcon } from 'lucide-react';
import { SearchParam } from '@common/ts/enums';
import { Button } from '@/components/ui/button';
import CopyButton from '@/components/ui/CopyButton';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { NavigationTabs } from '@/enums';
import useTranslation from '@/hooks/useTranslation';
import useResultTabStore from '@/store/ResultsTabStore';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import { useGetConversationPanel } from '@/reactQuery/get';
import TranscriptMessage from './TranscriptMessage';

function formatDuration(startedAt?: string, finishedAt?: string): string | null {
  if (!startedAt || !finishedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(finishedAt).getTime();
  const diffMs = end - start;
  if (diffMs <= 0) return null;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return '< 1 min';
  return `${mins} min`;
}

function formatDate(dateString?: string): string | null {
  if (!dateString) return null;
  // Honour the browser locale instead of hardcoding en-GB so a Dutch
  // user sees "8 apr. 2026 10:24" and an English user sees "8 Apr 2026,
  // 10:24" without a config change.
  return new Date(dateString).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMetadata(metadata?: Record<string, string>): string {
  if (!metadata) return '';
  const parts: string[] = [];
  if (metadata.gender) parts.push(metadata.gender);
  if (metadata.age) parts.push(metadata.age);
  if (parts.length === 0) {
    const values = Object.values(metadata)
      .filter((v) => typeof v === 'string' && v.length < 50)
      .slice(0, 2);
    parts.push(...values);
  }
  return parts.join(', ');
}

function TranscriptPanel() {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const { setActiveTab } = useResultTabStore();
  const { isOpen, conversationId, highlightMessageId, fallbackQuestionId, close } =
    useTranscriptPanelStore();

  const { data: conversation, isLoading } = useGetConversationPanel(projectId, conversationId);

  // Resolve the message to scroll to. Priority:
  //   1. Exact `highlightMessageId` from the caller (curated voice_quote
  //      with a backend-validated message_id) — most precise.
  //   2. First user message tagged with `relates_to === fallbackQuestionId`
  //      — used for per-question summary citations where we know which
  //      question the citation refers to but the cited respondent isn't
  //      in the curated voice_quotes set.
  //   3. First user message of the conversation — used for overall page
  //      citations where the citation tag carries no questionId. Better
  //      than landing at the top of a 30-message transcript with no
  //      visual anchor.
  //   4. No scroll — panel opens at the top.
  // The cascading fallback ensures every citation in the per-question
  // and overall summaries gets *some* scroll-to behaviour, instead of
  // silently doing nothing for respondents outside the voice_quotes
  // selection.
  const effectiveHighlightMessageId = (() => {
    if (highlightMessageId) return highlightMessageId;
    if (!conversation?.messages) return null;
    if (fallbackQuestionId) {
      // E9 / audit F-6: relates_to is now an optional field on
      // MessageData (see types.d.ts). The `as any` cast is gone.
      const questionMsg = conversation.messages.find(
        (m) => m.role === 'user' && m.relates_to === fallbackQuestionId,
      );
      if (questionMsg?._id) return questionMsg._id;
    }
    const firstUserMsg = conversation.messages.find((m) => m.role === 'user');
    return firstUserMsg?._id ?? null;
  })();

  const handleOpenFullTranscript = () => {
    if (!conversationId) return;
    close();
    setActiveTab(NavigationTabs.TRANSCRIPTS);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(SearchParam.SELECTED_TRANSCRIPT, conversationId);
      next.set(SearchParam.CONVERSATION_FROM_QUESTIONS_TAB, 'true');
      return next;
    });
  };

  const messages = conversation?.messages?.filter((m) => m.role !== 'system') || [];
  const duration = formatDuration(conversation?.startedAt, conversation?.finishedAt);
  const startDate = formatDate(conversation?.startedAt || conversation?.createdAt);
  const metaSummary = formatMetadata(conversation?.metadata);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <SheetContent
        side="right"
        // Panel sits below the fixed ProjectNavbar + Results tabs bar
        // (~108px combined) so its header isn't clipped behind them.
        // Uses inline style because Sheet's sheetVariants already
        // includes `inset-y-0` and we need to override top/height
        // without fighting Tailwind precedence.
        style={{ top: 108, bottom: 0, height: 'auto' }}
        className="w-full sm:w-[45%] sm:min-w-[400px] max-w-[600px] p-0 flex flex-col"
      >
        <SheetTitle className="sr-only">{lang.get('msg.transcriptPanelTitle')}</SheetTitle>

        {/* Header */}
        <div className="border-b border-neutral-200 px-5 py-4 shrink-0">
          <div className="flex items-center gap-2 min-w-0 mb-2 pr-8">
            {/* pr-8 leaves room for Radix's built-in close (X) button
                in the top-right corner of the SheetContent — we don't
                render our own to avoid duplicate close controls. */}
            <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/5 shrink-0">
              <UserIcon className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-neutral-900">
                {lang.get('msg.respondent')} #{conversation?.respondent_index ?? '—'}
              </p>
              {metaSummary && <p className="text-xs text-neutral-500 truncate">{metaSummary}</p>}
            </div>
          </div>

          {/* Conversation ID — shown so researchers can copy the exact
              MongoDB _id for debugging, citation resolution, or linking
              back into exports. Monospace + truncate keeps it tidy in a
              narrow panel. */}
          {conversationId && (
            <div className="flex items-center gap-2 mb-2 text-[11px] text-neutral-400">
              <span className="uppercase tracking-wide">ID</span>
              <code className="font-mono text-neutral-500 truncate">{conversationId}</code>
              <CopyButton text={conversationId} />
            </div>
          )}

          <div className="flex items-center gap-3 text-xs text-neutral-400">
            {startDate && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {startDate}
              </span>
            )}
            {duration && <span>{duration}</span>}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 py-3">
          {/* eslint-disable-next-line no-nested-ternary */}
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-8">
              {lang.get('msg.noMessagesFound')}
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {messages.map((msg) => (
                <TranscriptMessage
                  key={msg._id}
                  id={msg._id}
                  role={msg.role}
                  text={msg.text}
                  data={msg.data}
                  createdAt={msg.created_at}
                  isHighlighted={msg._id === effectiveHighlightMessageId}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-200 px-5 py-3 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOpenFullTranscript}
            // TELV1-310 / D3: `px-0` left no breathing room around the
            // hover background and clipped the icon against the panel
            // edge. `px-2 -mx-2` adds symmetric horizontal padding while
            // keeping the visible text flush with the panel's content
            // gutter (the negative margin offsets the new padding).
            className="text-xs text-neutral-500 hover:text-neutral-700 gap-1.5 px-2 -mx-2"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {lang.get('msg.openFullTranscript')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default TranscriptPanel;
